/**
 * Main Element:
 * data-overlay="unique-key"
 * data-overlay-ignore-escape
 *
 * Buttons:
 * data-overlay-toggle="unique-key"
 * data-overlay-open="unique-key"
 * data-overlay-close="unique-key"
 *
 * CSS Classes Applied to Body:
 * overlay-unique-key-opening
 * overlay-unique-key-open
 * overlay-unique-key-closing
 * some-overlay-open
 *
 * Accessibility:
 * - Overlays are rendered with `role=dialog aria-modal=true` and start `inert`.
 * - Opening removes `inert` from the overlay, adds `inert` to every other
 *   body-level sibling (effectively trapping focus/AT inside the overlay),
 *   moves focus into the overlay, and syncs `aria-expanded=true` on any
 *   data-overlay-toggle/data-overlay-open button for this key.
 * - Closing reverses all of the above and restores focus to whichever
 *   element was focused before the overlay opened.
 */

const openDuration  = 10;
const closeDuration = 300;
const debugMode     = import.meta.env.DEV; // dev-only logging; stripped from prod builds

const log = (message) => debugMode && console.log(`[Overlay] ${message}`);

const state = { current: null };

// Per-key open/close/toggle controllers, so other modules can drive an overlay
// programmatically (e.g. newsletter.js opening the newsletter overlay from an
// intercepted link) via the exported helpers below.
const controllers = new Map();

let previouslyFocused = null;
let siblingsWeInerted = [];

const setTogglesExpanded = (key, expanded) => {
    const value = expanded ? 'true' : 'false';
    document.querySelectorAll(
        `[data-overlay-toggle="${key}"], [data-overlay-open="${key}"]`
    ).forEach((el) => el.setAttribute('aria-expanded', value));
};

/**
 * Walk up from the overlay to <body>, inerting each sibling along the way.
 * Overlays in this theme live inside scrollbar-wrapper > body-wrapper, so
 * we can't just inert direct body children — `inert` inherits down a subtree
 * and can't be overridden by a descendant.
 */
const lockBackground = (activeOverlay) => {
    siblingsWeInerted = [];
    let node = activeOverlay;
    while (node && node !== document.body) {
        const parent = node.parentElement;
        if (!parent) break;
        Array.from(parent.children).forEach((sibling) => {
            if (sibling === node) return;
            if (sibling.tagName === 'SCRIPT') return;
            if (sibling.inert) return; // already inert (another closed overlay) — leave it
            // The header floats above overlays (z-index 21 > 20) and holds the
            // overlay toggle buttons, so it must stay interactive — inerting it
            // would make clicks fall through to the overlay behind it.
            if (sibling.hasAttribute('data-overlay-keep-active')) return;
            sibling.inert = true;
            siblingsWeInerted.push(sibling);
        });
        node = parent;
    }
};

const unlockBackground = () => {
    siblingsWeInerted.forEach((el) => { el.inert = false; });
    siblingsWeInerted = [];
};

const moveFocusInto = (overlay) => {
    // Prefer an explicit target — single-purpose overlays (e.g. search) want
    // their field focused directly via data-overlay-autofocus. Otherwise focus
    // the dialog container itself: it carries role=dialog + aria-labelledby +
    // tabindex=-1, so AT announces the dialog's name and reading starts at the
    // top, instead of landing on the close button (the first focusable child in
    // content-rich overlays like newsletter/accessibility).
    const target = overlay.querySelector('[data-overlay-autofocus]') || overlay;
    target.focus({ preventScroll: false });
};

const openTransition = (overlayKey) => new Promise((resolve) => {
    document.body.classList.add(`overlay-${overlayKey}-opening`);
    setTimeout(() => {
        document.body.classList.add(`overlay-${overlayKey}-open`, `some-overlay-open`);
        resolve();
    }, openDuration);
});

const closeTransition = (overlayKey) => new Promise((resolve) => {
    document.body.classList.remove(`overlay-${overlayKey}-open`);
    document.body.classList.add(`overlay-${overlayKey}-closing`);
    setTimeout(() => {
        document.body.classList.remove(
            `overlay-${overlayKey}-closing`,
            `overlay-${overlayKey}-opening`,
            `some-overlay-open`
        );
        resolve();
    }, closeDuration);
});

document.querySelectorAll('[data-overlay]').forEach((overlay) => {
    const key            = overlay.getAttribute('data-overlay');
    const closeOnEscape  = !overlay.hasAttribute('data-overlay-ignore-escape');

    const closeOverlay = async (e) => {
        e?.preventDefault();
        if (state.current !== key) return;
        log(`Closing overlay: ${key}`);

        // Restore focus before we inert the overlay, so focus doesn't bounce
        // to <body> in the interim.
        const toRestore = previouslyFocused && document.body.contains(previouslyFocused)
            ? previouslyFocused
            : null;
        previouslyFocused = null;
        toRestore?.focus({ preventScroll: true });

        overlay.inert = true;
        unlockBackground();
        setTogglesExpanded(key, false);

        await closeTransition(key);
        state.current = null;

        window.dispatchEvent(new CustomEvent('overlay:closed', { detail: { key, open: false } }));
    };

    const openOverlay = async (e) => {
        e?.preventDefault();
        if (state.current === key) return;

        // Close any currently-open overlay first (visual + a11y).
        if (state.current) {
            log(`Auto-closing overlay: ${state.current}`);
            const prevKey     = state.current;
            const prevOverlay = document.querySelector(`[data-overlay="${prevKey}"]`);
            if (prevOverlay) {
                prevOverlay.inert = true;
                setTogglesExpanded(prevKey, false);
            }
            unlockBackground();
            await closeTransition(prevKey);
            state.current = null;
        }

        log(`Opening overlay: ${key}`);
        previouslyFocused = document.activeElement;

        overlay.inert = false;
        lockBackground(overlay);
        setTogglesExpanded(key, true);

        await openTransition(key);
        state.current = key;

        moveFocusInto(overlay);

        window.dispatchEvent(new CustomEvent('overlay:opened', { detail: { key, open: true } }));
    };

    const toggleOverlay = (e) => state.current === key ? closeOverlay(e) : openOverlay(e);

    controllers.set(key, { open: openOverlay, close: closeOverlay, toggle: toggleOverlay });

    document.querySelectorAll(`[data-overlay-toggle="${key}"]`).forEach((el) =>
        el.addEventListener('click', toggleOverlay)
    );

    document.querySelectorAll(`[data-overlay-open="${key}"]`).forEach((el) =>
        el.addEventListener('click', openOverlay)
    );

    [...document.querySelectorAll(`[data-overlay-close="${key}"]`),
     ...overlay.querySelectorAll('[data-overlay-close]')].forEach((el) =>
        el.addEventListener('click', closeOverlay)
    );

    closeOnEscape && document.addEventListener('keydown', (e) =>
        state.current === key && e.key === 'Escape' && closeOverlay(e)
    );

    log(`Initialized overlay: ${key}`);
});

log('Overlay script loaded');

/**
 * Programmatically open/close/toggle an overlay by its data-overlay key.
 * No-op if the key was never registered.
 */
export const openOverlayByKey   = (key) => controllers.get(key)?.open();
export const closeOverlayByKey  = (key) => controllers.get(key)?.close();
export const toggleOverlayByKey = (key) => controllers.get(key)?.toggle();
